import { useMemo } from 'react';

import { defaultBreakpointsDetails } from './defaultConfig';
import { useBreakpoint, UseBreakpointOptions } from './useBreakpoint';
import { getBreakpointsDetails } from './utils/getBreakpointsDetails';
import {
  arrayToBreakpointObject,
  getClosestBreakpointValue,
} from './utils/mediaQueryUtils';

/**
 * Use breakpoint value
 *
 * Hook to return different values based on the current breakpoint.
 *
 * @example
 *   const maxWidth = useBreakpointValue({
 *     base: '100%',
 *     md: '50%',
 *     lg: '25%',
 *   });
 *
 *   // maxWidth on breakpoint 'sm' is inherited from 'base'
 *
 *   // Can also be written in array notation where null returns the value from previous breakpoint
 *   const maxWidth = useBreakpointValue(['100%', null, '50%', '25%']);
 *
 *   // Or used for toggling design styles
 *   const isSimpleLayout = useBreakpointValue(
 *     {
 *       base: 'true',
 *       lg: false,
 *     },
 *     {
 *       fallback: 'base', // The breakpoint to use in SSR
 *     },
 *   );
 */
export function useBreakpointValue<TValue = unknown>(
  /** The values for each breakpoint */
  values: Partial<Record<string, TValue>> | TValue[],
  /** Breakpoint options like fallback breakpoint and custom breakpoints */
  arg?: UseBreakpointOptions,
): TValue | undefined {
  const { breakpoints } = arg ?? {};

  const breakpointDetails = useMemo(() => {
    if (!breakpoints) {
      return defaultBreakpointsDetails;
    }

    return getBreakpointsDetails(breakpoints);
  }, [breakpoints]);

  const breakpoint = useBreakpoint(arg);

  if (!breakpoint) return;

  const valuesBreakpointObject = Array.isArray(values)
    ? arrayToBreakpointObject(values, breakpointDetails.keys)
    : values;

  return getClosestBreakpointValue(
    valuesBreakpointObject,
    breakpoint,
    breakpointDetails.keys,
  );
}
